import { Box, IconButton } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { qk, updateProjectAvatar } from 'api'
import { IconEditFilled } from 'assets/icons'
import { ImageEditor } from 'components/global/image-editor'
import { Dialog } from 'elements/feedback'
import { ToastContent } from 'elements/feedback/toast'
import { useBoolean } from 'lib/hooks'
import { ProjectAvatar } from 'pages/projects/components/project-avatar'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

type ProjectAvatarBlockProps = {
  projectId: string
  projectName: string
  avatarSrc?: string
}

export const ProjectAvatarBlock = ({
  projectId,
  projectName,
  avatarSrc,
}: ProjectAvatarBlockProps) => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const isOpen = useBoolean()

  const $updateProjectAvatar = useMutation(updateProjectAvatar)

  const uploadProjectAvatar = (blob: Blob) => {
    $updateProjectAvatar.mutate(
      { projectId, file: blob },
      {
        onSuccess: () => {
          isOpen.setFalse()
          queryClient.invalidateQueries(
            qk.projects.details.toKeyWithArgs({ projectId }),
          )
          toast.success(
            <ToastContent
              title={t('projects.settings.project_image_successfully_changed')}
            />,
          )
        },
      },
    )
  }

  return (
    <>
      <Box position="relative">
        <ProjectAvatar name={projectName} size={60} imageSrc={avatarSrc} />

        <IconButton
          onClick={isOpen.setTrue}
          sx={{ position: 'absolute', bottom: -3, right: -5 }}
        >
          <IconEditFilled sx={{ color: 'primary.main' }} />
        </IconButton>
      </Box>

      <Dialog open={isOpen.isTrue} onClose={isOpen.setFalse}>
        <ImageEditor
          onClose={isOpen.setFalse}
          isLoading={$updateProjectAvatar.isLoading}
          onConfirm={uploadProjectAvatar}
          dialogTitle={t('projects.settings.change_project_image')}
        />
      </Dialog>
    </>
  )
}
